import { useEffect, useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import { Plus, Clock, Calendar as CalendarIcon, Edit2, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/utils/api";

const Attendance = () => {
  const { formatDate, formatCurrency } = useSettings();
  const [attendance, setAttendance] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0,10));
  const [filterStartDate, setFilterStartDate] = useState(new Date().toISOString().slice(0,10));
  const [filterEndDate, setFilterEndDate] = useState(new Date().toISOString().slice(0,10));
  const [filterMemberId, setFilterMemberId] = useState("all");
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkDate, setBulkDate] = useState(new Date().toISOString().slice(0,10));
  const [bulkTime, setBulkTime] = useState(() => {
    const d = new Date();
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  });
  const [checkInTime, setCheckInTime] = useState(() => {
    const d = new Date();
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  });
  const [isCheckoutDialogOpen, setIsCheckoutDialogOpen] = useState(false);
  const [checkoutAttendanceId, setCheckoutAttendanceId] = useState("");
  const [checkoutDate, setCheckoutDate] = useState("");
  const [checkoutTime, setCheckoutTime] = useState(() => {
    const d = new Date();
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  });
  
  // Edit State
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [editCheckInTime, setEditCheckInTime] = useState("");
  const [editCheckOutTime, setEditCheckOutTime] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [attendanceRes, membersRes] = await Promise.all([
        api.get('/attendance'),
        api.get('/members'),
      ]);
      setAttendance(attendanceRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const checkInIso = checkInTime ? new Date(`${selectedDate}T${checkInTime}`).toISOString() : undefined;
      await api.post('/attendance', {
        member_id: selectedMemberId,
        date: selectedDate,
        check_in_time: checkInIso,
      });
      toast.success("Attendance marked successfully");
      fetchData();
      setIsDialogOpen(false);
      setSelectedMemberId("");
      setSelectedDate(new Date().toISOString().slice(0,10));
      const d = new Date();
      const h = String(d.getHours()).padStart(2, '0');
      const m = String(d.getMinutes()).padStart(2, '0');
      setCheckInTime(`${h}:${m}`);
    } catch (error) {
      toast.error("Failed to mark attendance");
    }
  };

  const openCheckoutDialog = (record) => {
    setCheckoutAttendanceId(record.id);
    setCheckoutDate(record.date);
    const d = new Date();
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    setCheckoutTime(`${h}:${m}`);
    setIsCheckoutDialogOpen(true);
  };

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    try {
      const iso = new Date(`${checkoutDate}T${checkoutTime}`).toISOString();
      await api.patch(`/attendance/${checkoutAttendanceId}/checkout`, {
        check_out_time: iso,
      });
      toast.success("Checkout recorded successfully");
      fetchData();
      setIsCheckoutDialogOpen(false);
      setCheckoutAttendanceId("");
    } catch (error) {
      toast.error("Failed to mark checkout");
    }
  };

  const handleEditClick = (record) => {
    setEditingAttendance(record);
    setEditDate(record.date);
    
    const checkInDate = new Date(record.check_in_time);
    setEditCheckInTime(`${String(checkInDate.getHours()).padStart(2, '0')}:${String(checkInDate.getMinutes()).padStart(2, '0')}`);
    
    if (record.check_out_time) {
      const checkOutDate = new Date(record.check_out_time);
      setEditCheckOutTime(`${String(checkOutDate.getHours()).padStart(2, '0')}:${String(checkOutDate.getMinutes()).padStart(2, '0')}`);
    } else {
      setEditCheckOutTime("");
    }
    
    setIsEditOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const checkInIso = editCheckInTime ? new Date(`${editDate}T${editCheckInTime}`).toISOString() : undefined;
      const checkOutIso = editCheckOutTime ? new Date(`${editDate}T${editCheckOutTime}`).toISOString() : null;
      
      await api.put(`/attendance/${editingAttendance.id}`, {
        date: editDate,
        check_in_time: checkInIso,
        check_out_time: checkOutIso
      });
      
      toast.success("Attendance updated successfully");
      fetchData();
      setIsEditOpen(false);
      setEditingAttendance(null);
    } catch (error) {
      toast.error("Failed to update attendance");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this attendance record?")) return;
    
    try {
      await api.delete(`/attendance/${id}`);
      toast.success("Attendance deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete attendance");
    }
  };

  const getMemberName = (memberId) => {
    const member = members.find((m) => m.id === memberId);
    return member ? member.name : "Unknown";
  };

  const clearFilters = () => {
    const today = new Date().toISOString().slice(0,10);
    setFilterStartDate(today);
    setFilterEndDate(today);
    setFilterMemberId("all");
  };

  const displayAttendance = attendance.filter((record) => {
    const d = record.date;
    if (filterStartDate && d < filterStartDate) return false;
    if (filterEndDate && d > filterEndDate) return false;
    if (filterMemberId !== "all" && record.member_id !== filterMemberId) return false;
    return true;
  });

  const toggleBulkMember = (id) => {
    setBulkSelectedIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleBulkAll = () => {
    const activeIds = members.filter(m => m.status === 'active').map(m => m.id);
    const allSelected = bulkSelectedIds.length === activeIds.length;
    setBulkSelectedIds(allSelected ? [] : activeIds);
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (bulkSelectedIds.length === 0) {
      toast.error("Please select at least one member");
      return;
    }
    const isoTime = new Date(`${bulkDate}T${bulkTime}`).toISOString();
    const results = await Promise.allSettled(
      bulkSelectedIds.map((member_id) =>
        api.post('/attendance', { member_id, date: bulkDate, check_in_time: isoTime })
      )
    );
    const successCount = results.filter(r => r.status === 'fulfilled').length;
    const failCount = results.length - successCount;
    if (successCount) toast.success(`Marked ${successCount} attendance${successCount>1?"s":""}`);
    if (failCount) toast.error(`${failCount} failed`);
    fetchData();
    setIsBulkDialogOpen(false);
    setBulkSelectedIds([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  return (
    <div data-testid="attendance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Attendance
          </h1>
          <p className="text-zinc-500">Track member attendance</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="mark-attendance-btn"
                className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold shadow-sm transition-all active:scale-95"
              >
                <Plus size={20} className="mr-2" />
                Mark Attendance
              </Button>
            </DialogTrigger>
            <DialogContent aria-describedby="attendance-dialog-description">
              <DialogHeader>
                <DialogTitle>Mark Attendance</DialogTitle>
              </DialogHeader>
              <p id="attendance-dialog-description" className="sr-only">
                Mark attendance for a gym member
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="member">Select Member</Label>
                  <Select
                    value={selectedMemberId}
                    onValueChange={setSelectedMemberId}
                    required
                  >
                    <SelectTrigger data-testid="member-select">
                      <SelectValue placeholder="Choose a member" />
                    </SelectTrigger>
                    <SelectContent>
                      {members.filter(m => m.status === 'active').map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="attendance-date">Attendance Date</Label>
                  <Input
                    id="attendance-date"
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="attendance-time">Check-in Time</Label>
                  <Input
                    id="attendance-time"
                    type="time"
                    value={checkInTime}
                    onChange={(e) => setCheckInTime(e.target.value)}
                    className="mt-2"
                  />
                </div>
                <Button 
                  type="submit" 
                  data-testid="attendance-submit-btn"
                  className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
                  disabled={!selectedMemberId}
                >
                  Mark Present
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-zinc-900 text-white hover:bg-zinc-800 font-bold shadow-sm transition-all active:scale-95"
              >
                <Plus size={20} className="mr-2" />
                Mark Multiple Attendance
              </Button>
            </DialogTrigger>
          <DialogContent aria-describedby="bulk-attendance-dialog-description">
            <DialogHeader>
              <DialogTitle>Mark Multiple Attendance</DialogTitle>
            </DialogHeader>
            <p id="bulk-attendance-dialog-description" className="sr-only">
              Mark attendance for multiple gym members
            </p>
            <form onSubmit={handleBulkSubmit} className="space-y-4">
              <div className="flex items-center gap-2">
                <Checkbox id="bulk-select-all" checked={bulkSelectedIds.length === members.filter(m=>m.status==='active').length && bulkSelectedIds.length>0} onCheckedChange={toggleBulkAll} />
                <Label htmlFor="bulk-select-all">Select All Active Members</Label>
              </div>
              <div className="max-h-64 overflow-auto border rounded-md p-2">
                {members.filter(m => m.status === 'active').map((member) => (
                  <div key={member.id} className="flex items-center gap-2 py-1">
                    <Checkbox id={`bulk-${member.id}`} checked={bulkSelectedIds.includes(member.id)} onCheckedChange={() => toggleBulkMember(member.id)} />
                    <Label htmlFor={`bulk-${member.id}`}>{member.name}</Label>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bulk-date">Date</Label>
                  <Input id="bulk-date" type="date" value={bulkDate} onChange={(e)=>setBulkDate(e.target.value)} required className="mt-2" />
                </div>
                <div>
                  <Label htmlFor="bulk-time">Check-in Time</Label>
                  <Input id="bulk-time" type="time" value={bulkTime} onChange={(e)=>setBulkTime(e.target.value)} className="mt-2" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold" disabled={bulkSelectedIds.length===0 || isSubmitting}>
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Marking...</span>
                  </div>
                ) : (
                  "Mark Attendance for Selected"
                )}
              </Button>
            </form>
          </DialogContent></Dialog>
        </div>
      </div>

      <div className="mb-4 bg-white border border-zinc-200 rounded-lg p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="filter-start">From Date</Label>
            <Input
              id="filter-start"
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="filter-end">To Date</Label>
            <Input
              id="filter-end"
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="filter-member">Member</Label>
            <Select value={filterMemberId} onValueChange={setFilterMemberId}>
              <SelectTrigger id="filter-member" className="mt-2">
                <SelectValue placeholder="All Members" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                {members.filter(m => m.status === 'active').map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={clearFilters} className="w-full">Clear Filters</Button>
          </div>
        </div>
      </div>
      {/* Attendance Table */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap">
            <thead className="bg-zinc-50 text-zinc-500 font-medium text-xs uppercase tracking-wider">
            <tr>
              <th className="px-6 py-4 text-left">Member Name</th>
              <th className="px-6 py-4 text-left">Date</th>
              <th className="px-6 py-4 text-left">Check-in Time</th>
              <th className="px-6 py-4 text-left">Check-out Time</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayAttendance.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center text-zinc-500">
                  No attendance records found for selected date(s).
                </td>
              </tr>
            ) : (
              displayAttendance.map((record) => (
                <tr
                  key={record.id}
                  data-testid={`attendance-row-${record.id}`}
                  className="hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0"
                >
                  <td className="px-6 py-4">
                    <div className="font-medium text-zinc-900">{getMemberName(record.member_id)}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-600">
                      <CalendarIcon size={14} />
                      {formatDate(record.date)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-600">
                      <Clock size={14} />
                      {new Date(record.check_in_time).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-zinc-600">
                      {record.check_out_time 
                        ? new Date(record.check_out_time).toLocaleTimeString()
                        : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openCheckoutDialog(record)}
                          >
                            Mark Checkout
                          </Button>
                        )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditClick(record)}
                        className="h-8 w-8 text-zinc-500 hover:text-zinc-900"
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(record.id)}
                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
      <Dialog open={isCheckoutDialogOpen} onOpenChange={setIsCheckoutDialogOpen}>
        <DialogContent aria-describedby="checkout-dialog-description">
          <DialogHeader>
            <DialogTitle>Mark Checkout</DialogTitle>
          </DialogHeader>
          <p id="checkout-dialog-description" className="sr-only">
            Mark checkout time for attendance record
          </p>
          <form onSubmit={handleCheckoutSubmit} className="space-y-4">
            <div>
              <Label htmlFor="checkout-time">Checkout Time</Label>
              <Input
                id="checkout-time"
                type="time"
                value={checkoutTime}
                onChange={(e) => setCheckoutTime(e.target.value)}
                className="mt-2"
                required
              />
            </div>
            <Button type="submit" className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold">
              Confirm Checkout
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Attendance</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label>Member</Label>
              <div className="p-2 bg-zinc-50 rounded-md border border-zinc-200 mt-2">
                {editingAttendance && getMemberName(editingAttendance.member_id)}
              </div>
            </div>
            <div>
              <Label htmlFor="edit-date">Date</Label>
              <Input
                id="edit-date"
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                required
                className="mt-2"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-checkin">Check-in Time</Label>
                <Input
                  id="edit-checkin"
                  type="time"
                  value={editCheckInTime}
                  onChange={(e) => setEditCheckInTime(e.target.value)}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="edit-checkout">Check-out Time</Label>
                <Input
                  id="edit-checkout"
                  type="time"
                  value={editCheckOutTime}
                  onChange={(e) => setEditCheckOutTime(e.target.value)}
                  className="mt-2"
                />
              </div>
            </div>
            <Button type="submit" className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold">
              Update Attendance
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Attendance;
